import { WatchQueryFetchPolicy } from '@apollo/client';

import { useStudentQuery } from 'lib/apollo/student-apollo-hooks';
import {
  ActivitiesFontSize,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import { useActivityBuilderTheme } from '../../activity-builder/theme/activity-builder-theme';
import { GET_STUDENT_ASSESSMENT_ASSIGNMENT_BY_ID } from '../get-student-assessment-assignment-by-id.graphql';
import type {
  GetStudentAssessmentAssignmentByIdQuery,
  GetStudentAssessmentAssignmentByIdQueryVariables,
} from '../get-student-assessment-assignment-by-id.graphql.generated';
import {
  StudentAssessmentActivities,
  StudentAssignmentAssessmentLinkedContent,
} from '../student-assessment.constants';

type GetStudentAssessmentAssignmentByIdProps = {
  assignmentId: number;
  classId: number | undefined;
  language?: LanguageCodes;
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
};

export const useGetStudentAssessmentAssignmentById = ({
  assignmentId,
  classId,
  language,
  fetchPolicy = 'cache-first',
  skip,
}: GetStudentAssessmentAssignmentByIdProps) => {
  const { updateFontSize } = useActivityBuilderTheme();

  const { data, loading, error } = useStudentQuery<
    GetStudentAssessmentAssignmentByIdQuery,
    GetStudentAssessmentAssignmentByIdQueryVariables
  >(GET_STUDENT_ASSESSMENT_ASSIGNMENT_BY_ID, {
    skip: skip || !assignmentId || !classId,
    fetchPolicy,
    variables: {
      input: {
        assignmentId,
        classId: classId!,
        language: language ?? LANGUAGE_CODE_EN_AU,
      },
    },
    onCompleted: res => {
      const activities = res.getStudentAssignmentById.assignedActivities;
      if (
        activities?.__typename === 'AssessmentActivities' &&
        activities.styles
      ) {
        updateFontSize(activities.styles.fontSize as ActivitiesFontSize);
      }
    },
  });

  const assignedActivities = data?.getStudentAssignmentById?.assignedActivities;

  return {
    assignment: data?.getStudentAssignmentById,
    assignedActivities: assignedActivities as
      | StudentAssessmentActivities
      | undefined,
    linkedContent: data?.getStudentAssignmentById?.linkedContent as
      | StudentAssignmentAssessmentLinkedContent
      | undefined,
    loading,
    error,
  };
};
